import { lazy, Suspense, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowDownToLine,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Landmark,
  Receipt,
  Wallet,
} from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { StatCard } from '@/components/StatCard'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { DataTable } from '@/components/DataTable'
import { Modal } from '@/components/Modal'
import { Toolbar } from '@/components/Toolbar'
import { formatDate, getUserLocale } from '@/lib/locale'
import { ApiError } from '@/lib/api'
import { usePaymentsDashboard, usePayoutItems } from '@/features/invoicing/payments/queries'
import { paymentsApi } from '@/features/invoicing/payments/api'
import { useOpenStripeDashboard } from '@/features/invoicing/queries'
import { useToast } from '@/hooks/useToast'
import { useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import {
  RefundActionButton,
  RefundStatusBadge,
} from '@/features/invoicing/payments/RefundControls'
import {
  bpsToPercent,
  formatMoneyCents,
  formatStripeBalance,
} from '@/features/invoicing/payments/format'
import type {
  ConnectPaymentRow,
  ConnectPayoutRow,
  ManagedPaymentRow,
  PaymentsDashboardConnect,
  PaymentsDashboardManaged,
  PayoutItemRow,
  PayoutRow,
  PendingBalanceRow,
  RefundRow,
} from '@/features/invoicing/payments/types'

// Lazy-load the embedded Stripe Connect panels - adds ~150KB to the
// page's chunk only when a connect_express tenant scrolls down to it.
const StripeConnectEmbeddedPanel = lazy(() =>
  import('@/features/invoicing/payments/StripeConnectEmbeddedPanel').then((m) => ({
    default: m.StripeConnectEmbeddedPanel,
  })),
)


// ── Page ──────────────────────────────────────────────────────────────

export function PaymentsDashboardPage() {
  const q = usePaymentsDashboard()

  if (q.isLoading) {
    return (
      <Page title="Payments">
        <Card><CardContent className="py-10"><LoadingSpinner /></CardContent></Card>
      </Page>
    )
  }
  if (q.isError) {
    return (
      <Page title="Payments">
        <Alert variant="destructive">
          <AlertTitle>Could not load payments</AlertTitle>
          <AlertDescription>
            {q.error instanceof ApiError
              ? (q.error.body && typeof q.error.body === 'object' && 'error' in q.error.body
                  ? String((q.error.body as { error: unknown }).error)
                  : q.error.message)
              : (q.error as Error).message}
          </AlertDescription>
        </Alert>
      </Page>
    )
  }

  const data = q.data!

  if (data.mode == null) {
    return (
      <Page title="Payments" description="Track customer payments, fees, and payouts.">
        <Card>
          <CardContent className="py-10">
            <EmptyState
              icon={Wallet}
              title="Pick a payment collection mode first"
              description="Decide whether to collect through Stripe Connect (money lands in your bank directly) or Bokapsys-managed (we collect and pay you out twice monthly). Until you choose, payment links can't be created."
              action={
                <Button asChild>
                  <Link to="/company">
                    <CreditCard className="size-4" /> Configure in Company settings
                  </Link>
                </Button>
              }
            />
          </CardContent>
        </Card>
      </Page>
    )
  }

  if (data.mode === 'bokapsys_managed') {
    return <ManagedDashboard data={data} />
  }
  return <ConnectDashboard data={data} />
}

// ── Bokapsys-managed mode ─────────────────────────────────────────────

function ManagedDashboard({ data }: { data: PaymentsDashboardManaged }) {
  const totalPending = data.pendingBalance.reduce((sum, b) => sum + b.netCents, 0)
  // Empty-balance fallback: use the tenant's regional currency instead
  // of hardcoded USD so a UK tenant with no pending balance sees £0.00
  // in the header tile, not $0.00.
  const primaryCurrency = data.pendingBalance[0]?.currency ?? getUserLocale().currency

  // Format the next payout date as e.g. "May 5 (in 7 days)" so the
  // tenant immediately sees both the calendar date AND the relative
  // distance. Computed server-side from the oldest unpaid payment.
  const nextPayoutLabel = (() => {
    if (!data.nextPayoutAt) return '-'
    const d = new Date(data.nextPayoutAt)
    const dayMs = 24 * 60 * 60 * 1000
    const days = Math.max(0, Math.ceil((d.getTime() - Date.now()) / dayMs))
    return formatDate(data.nextPayoutAt)
      + (days > 0 ? ` (in ${days} day${days === 1 ? '' : 's'})` : ' (due now)')
  })()

  return (
    <Page
      title="Payments"
      description="Bokapsys collects on your behalf and pays you out weekly."
      width="wide"
    >
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          label="Pending net"
          value={data.pendingBalance.length === 0
            ? '-'
            : formatMoneyCents(totalPending, primaryCurrency)}
          hint={data.pendingBalance.length > 1 ? 'across multiple currencies' : 'next payout'}
          icon={Wallet}
        />
        <StatCard
          label="Bokapsys fee"
          value={bpsToPercent(data.feeBps)}
          hint="of gross, deducted at payout"
          icon={Receipt}
        />
        <StatCard
          label="Next payout"
          value={nextPayoutLabel}
          hint={data.payoutCadence}
          icon={ArrowDownToLine}
        />
        <StatCard
          label="Stripe fee"
          value="passthrough"
          hint="at cost, deducted at payout"
          icon={CreditCard}
        />
      </div>

      <PendingBalanceCard rows={data.pendingBalance} feeBps={data.feeBps} />

      <PayoutDestinationCard destination={data.payoutDestination} />

      <RecentManagedPaymentsCard rows={data.recentPayments} />

      <RefundsCard rows={data.refunds} />

      <PayoutHistoryCard rows={data.payouts} />
    </Page>
  )
}

function PendingBalanceCard({
  rows,
  feeBps,
}: {
  rows: PendingBalanceRow[]
  feeBps: number
}) {
  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <EmptyState
            icon={CheckCircle2}
            title="No pending balance"
            description="No new customer payments since your last payout. New payments will accrue here."
          />
        </CardContent>
      </Card>
    )
  }
  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold">Pending balance - fee breakdown</h3>
            <p className="text-sm text-muted-foreground">
              Bokapsys fee is {bpsToPercent(feeBps)} of gross. Stripe processing fees pass through at cost.
            </p>
          </div>
        </div>
        <div className="grid gap-3">
          {rows.map((row) => (
            <div
              key={row.currency}
              className="rounded-md border bg-muted/30 p-4"
            >
              <div className="mb-2 flex flex-wrap items-center gap-3">
                <Badge variant="default">{row.currency}</Badge>
                <span className="text-sm text-muted-foreground">
                  {row.paymentCount} payment{row.paymentCount === 1 ? '' : 's'} pending
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                <FeeRow label="Customers paid" amountCents={row.grossCents} currency={row.currency} positive />
                <FeeRow
                  label="Refunded"
                  amountCents={-row.refundedCents}
                  currency={row.currency}
                  muted
                  danger={row.refundedCents > 0}
                />
                <FeeRow label="Stripe fee" amountCents={-row.stripeFeeCents} currency={row.currency} muted />
                <FeeRow label="Bokapsys fee" amountCents={-row.bokapsysFeeCents} currency={row.currency} muted />
                <FeeRow label="You receive" amountCents={row.netCents} currency={row.currency} bold />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function FeeRow({
  label,
  amountCents,
  currency,
  positive,
  muted,
  bold,
  danger,
}: {
  label: string
  amountCents: number
  currency: string
  positive?: boolean
  muted?: boolean
  bold?: boolean
  danger?: boolean
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className={[
          'mt-1 font-mono text-sm',
          bold ? 'text-base font-semibold' : '',
          muted ? 'text-muted-foreground' : '',
          positive ? 'text-foreground' : '',
          danger ? 'text-destructive' : '',
        ].filter(Boolean).join(' ')}
      >
        {amountCents < 0 ? '−' : ''}
        {formatMoneyCents(Math.abs(amountCents), currency)}
      </div>
    </div>
  )
}

function PayoutDestinationCard({ destination }: { destination: PaymentsDashboardManaged['payoutDestination'] }) {
  if (!destination) {
    return (
      <Alert>
        <AlertTriangle className="size-4" />
        <AlertTitle>Payout destination not configured</AlertTitle>
        <AlertDescription>
          We can&apos;t pay you out until you tell us where. Add a bank account in Company →
          Payment Collection.
          <div className="mt-3">
            <Button asChild size="sm">
              <Link to="/company"><Landmark className="size-4" /> Configure payout account</Link>
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    )
  }
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
        <div className="flex items-center gap-3">
          <Landmark className="size-5 text-muted-foreground" />
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Paying you to</div>
            <div className="font-semibold">
              {destination.bankName ? `${destination.bankName} · ` : ''}
              {destination.label}
            </div>
            <div className="text-xs text-muted-foreground">
              {destination.accountNumberLast4
                ? `Acct ••••${destination.accountNumberLast4}`
                : (destination.iban || destination.swift || '')}
            </div>
          </div>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/company">Update</Link>
        </Button>
      </CardContent>
    </Card>
  )
}

function RecentManagedPaymentsCard({ rows }: { rows: ManagedPaymentRow[] }) {
  const { toast } = useToast()
  const tenantId = useTenant()
  const qc = useQueryClient()
  const [refreshing, setRefreshing] = useState(false)
  // True when at least one settled row has no Stripe fee stamped yet -
  // common for payments that landed before fee-tagging was wired up
  // at the webhook layer. The Refresh button calls the same
  // backfill endpoint as the Connect-mode "Stripe deep dive" section.
  const hasMissingFees = rows.some((r) => r.stripeFeeCents === 0 && r.method === 'stripe')

  const onRefresh = async () => {
    setRefreshing(true)
    try {
      const r = await paymentsApi.backfillFees()
      toast({
        title: 'Stripe fees refreshed',
        description: `Stamped ${r.tagged} of ${r.scanned} payment${r.scanned === 1 ? '' : 's'} (${r.skipped} already had fees).`,
      })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'payments') })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Refresh failed',
        description: err instanceof Error ? err.message : 'unknown',
      })
    } finally {
      setRefreshing(false)
    }
  }

  if (rows.length === 0) {
    return null
  }
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-base font-semibold">Recent payments</h3>
          {hasMissingFees ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing…' : 'Refresh Stripe fees'}
            </Button>
          ) : null}
        </div>
        <DataTable
          rows={rows}
          columns={[
            {
              header: 'Date',
              accessorKey: 'receivedAt',
              cell: ({ row }) => (
                <span className="text-xs">{formatDate(row.original.receivedAt)}</span>
              ),
            },
            {
              header: 'Invoice',
              accessorKey: 'invoiceNumber',
              cell: ({ row }) => row.original.invoiceNumber ? (
                <Link
                  to={`/invoicing/invoices/${row.original.invoiceId}`}
                  className="text-primary hover:underline"
                >
                  {row.original.invoiceNumber}
                </Link>
              ) : '-',
            },
            { header: 'Customer', accessorKey: 'customerName',
              cell: ({ row }) => row.original.customerName ?? '-' },
            {
              header: () => <span className="block text-right">Gross</span>,
              accessorKey: 'grossCents',
              cell: ({ row }) => (
                <span className="block text-right font-mono text-xs">
                  {formatMoneyCents(row.original.grossCents, row.original.currency)}
                </span>
              ),
            },
            {
              header: () => <span className="block text-right">Stripe fee</span>,
              accessorKey: 'stripeFeeCents',
              cell: ({ row }) => (
                <span className="block text-right font-mono text-xs text-muted-foreground">
                  {row.original.stripeFeeCents
                    ? `−${formatMoneyCents(row.original.stripeFeeCents, row.original.currency)}`
                    : '-'}
                </span>
              ),
            },
            {
              header: () => <span className="block text-right">Bokapsys fee</span>,
              accessorKey: 'bokapsysFeeCents',
              cell: ({ row }) => (
                <span className="block text-right font-mono text-xs text-muted-foreground">
                  {row.original.bokapsysFeeCents
                    ? `−${formatMoneyCents(row.original.bokapsysFeeCents, row.original.currency)}`
                    : '-'}
                </span>
              ),
            },
            {
              header: () => <span className="block text-right">Net</span>,
              accessorKey: 'netCents',
              cell: ({ row }) => (
                <span className="block text-right font-mono text-xs font-semibold">
                  {formatMoneyCents(row.original.netCents, row.original.currency)}
                </span>
              ),
            },
            {
              header: 'Status',
              accessorKey: 'paid',
              cell: ({ row }) => (
                <span className="flex flex-wrap items-center gap-1">
                  {row.original.paid ? (
                    <Badge variant="success">Paid out</Badge>
                  ) : (
                    <Badge variant="secondary">Pending payout</Badge>
                  )}
                  <RefundStatusBadge
                    refundedCents={row.original.refundedCents}
                    grossCents={row.original.grossCents}
                    currency={row.original.currency}
                  />
                </span>
              ),
            },
            {
              header: '',
              id: 'actions',
              cell: ({ row }) => (
                <RefundActionButton
                  payment={{
                    id: row.original.id,
                    invoiceNumber: row.original.invoiceNumber,
                    customerName: row.original.customerName,
                    grossCents: row.original.grossCents,
                    refundedCents: row.original.refundedCents,
                    currency: row.original.currency,
                    method: row.original.method,
                  }}
                />
              ),
            },
          ]}
        />
      </CardContent>
    </Card>
  )
}

function PayoutHistoryCard({ rows }: { rows: PayoutRow[] }) {
  const [openPayoutId, setOpenPayoutId] = useState<number | null>(null)

  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <EmptyState
            icon={ArrowDownToLine}
            title="No payouts yet"
            description="Once Bokapsys processes a payout, it'll appear here with the full fee breakdown."
          />
        </CardContent>
      </Card>
    )
  }
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <h3 className="text-base font-semibold">Payout history</h3>
        <DataTable
          rows={rows}
          columns={[
            {
              header: 'Paid on',
              accessorKey: 'paidAt',
              cell: ({ row }) => (
                <span className="text-xs">
                  {row.original.paidAt
                    ? formatDate(row.original.paidAt)
                    : (row.original.createdAt ? formatDate(row.original.createdAt) : '-')}
                </span>
              ),
            },
            {
              header: 'Method',
              accessorKey: 'method',
              cell: ({ row }) => row.original.method
                ? <Badge variant="outline">{row.original.method.replace(/_/g, ' ')}</Badge>
                : '-',
            },
            {
              header: 'Reference',
              accessorKey: 'reference',
              cell: ({ row }) => row.original.reference ? (
                <span className="text-xs font-mono">{row.original.reference}</span>
              ) : '-',
            },
            {
              header: () => <span className="block text-right">Gross</span>,
              accessorKey: 'grossCents',
              cell: ({ row }) => (
                <span className="block text-right font-mono text-xs">
                  {formatMoneyCents(row.original.grossCents, row.original.currency)}
                </span>
              ),
            },
            {
              header: () => <span className="block text-right">Stripe fee</span>,
              accessorKey: 'stripeFeeCents',
              cell: ({ row }) => (
                <span className="block text-right font-mono text-xs text-muted-foreground">
                  −{formatMoneyCents(row.original.stripeFeeCents, row.original.currency)}
                </span>
              ),
            },
            {
              header: () => <span className="block text-right">Bokapsys fee</span>,
              accessorKey: 'bokapsysFeeCents',
              cell: ({ row }) => (
                <span className="block text-right font-mono text-xs text-muted-foreground">
                  −{formatMoneyCents(row.original.bokapsysFeeCents, row.original.currency)}
                </span>
              ),
            },
            {
              header: () => <span className="block text-right">Net to you</span>,
              accessorKey: 'netCents',
              cell: ({ row }) => (
                <span className="block text-right font-mono text-xs font-semibold">
                  {formatMoneyCents(row.original.netCents, row.original.currency)}
                </span>
              ),
            },
            {
              header: 'Status',
              accessorKey: 'status',
              cell: ({ row }) => {
                const v = row.original.status === 'paid' ? 'success'
                  : row.original.status === 'failed' ? 'danger'
                  : 'secondary'
                return <Badge variant={v}>{row.original.status}</Badge>
              },
            },
            {
              header: '',
              id: 'actions',
              cell: ({ row }) => (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  onClick={() => setOpenPayoutId(row.original.id)}
                >
                  View
                </Button>
              ),
            },
          ]}
        />
      </CardContent>
      <PayoutItemsModal
        payoutId={openPayoutId}
        onClose={() => setOpenPayoutId(null)}
      />
    </Card>
  )
}

function PayoutItemsModal({ payoutId, onClose }: { payoutId: number | null; onClose: () => void }) {
  const q = usePayoutItems(payoutId)
  return (
    <Modal open={payoutId != null} onOpenChange={(v) => !v && onClose()} title="Payments included in this payout" className="max-w-3xl">
      {q.isLoading ? <LoadingSpinner /> : null}
      {q.isError ? (
        <Alert variant="destructive">
          <AlertDescription>Could not load payout items.</AlertDescription>
        </Alert>
      ) : null}
      {q.data ? <PayoutItemsTable rows={q.data.items} /> : null}
    </Modal>
  )
}

function PayoutItemsTable({ rows }: { rows: PayoutItemRow[] }) {
  if (rows.length === 0) return <EmptyState title="No items" description="This payout has no associated payments." />
  return (
    <DataTable
      rows={rows}
      columns={[
        {
          header: 'Date',
          accessorKey: 'receivedAt',
          cell: ({ row }) => <span className="text-xs">{formatDate(row.original.receivedAt)}</span>,
        },
        {
          header: 'Invoice',
          accessorKey: 'invoiceNumber',
          cell: ({ row }) => row.original.invoiceNumber ? (
            <Link
              to={`/invoicing/invoices/${row.original.invoiceId}`}
              className="text-primary hover:underline"
            >
              {row.original.invoiceNumber}
            </Link>
          ) : '-',
        },
        { header: 'Customer', accessorKey: 'customerName',
          cell: ({ row }) => row.original.customerName ?? '-' },
        {
          header: () => <span className="block text-right">Gross</span>,
          accessorKey: 'grossCents',
          cell: ({ row }) => (
            <span className="block text-right font-mono text-xs">
              {formatMoneyCents(row.original.grossCents, row.original.currency)}
            </span>
          ),
        },
        {
          header: () => <span className="block text-right">Net</span>,
          accessorKey: 'netCents',
          cell: ({ row }) => (
            <span className="block text-right font-mono text-xs font-semibold">
              {formatMoneyCents(row.original.netCents, row.original.currency)}
            </span>
          ),
        },
      ]}
    />
  )
}

// ── Connect mode ──────────────────────────────────────────────────────

function ConnectDashboard({ data }: { data: PaymentsDashboardConnect }) {
  const [showEmbedded, setShowEmbedded] = useState(false)
  const totalAvailable = useMemo(() =>
    data.balance.available.reduce((s, b) => s + b.amountCents, 0),
    [data.balance.available],
  )
  // Same regional fallback as ManagedDashboard above - empty balance
  // arrays should still render in the tenant's currency.
  const primaryCurrency = data.balance.available[0]?.currency
    ?? data.balance.pending[0]?.currency
    ?? getUserLocale().currency
  const totalPending = useMemo(() =>
    data.balance.pending.reduce((s, b) => s + b.amountCents, 0),
    [data.balance.pending],
  )

  return (
    <Page
      title="Payments"
      description="Money lands in your Stripe account and pays out to your bank automatically."
      width="wide"
    >
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard
          label="Available"
          value={formatStripeBalance(totalAvailable, primaryCurrency)}
          hint="ready to pay out"
          icon={Wallet}
        />
        <StatCard
          label="Pending"
          value={formatStripeBalance(totalPending, primaryCurrency)}
          hint="settling on Stripe"
          icon={CreditCard}
        />
        <StatCard
          label="Charges"
          value={data.chargesEnabled ? 'Enabled' : 'Disabled'}
          icon={data.chargesEnabled ? CheckCircle2 : AlertTriangle}
        />
        <StatCard
          label="Payouts"
          value={data.payoutsEnabled ? 'Enabled' : 'Disabled'}
          icon={data.payoutsEnabled ? CheckCircle2 : AlertTriangle}
        />
      </div>

      {!data.chargesEnabled || !data.payoutsEnabled ? (
        <Alert variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertTitle>Stripe onboarding incomplete</AlertTitle>
          <AlertDescription>
            Stripe still needs information from you before it&apos;ll release payouts to
            your bank. Click <strong>Continue Stripe onboarding</strong> below.
            <div className="mt-3">
              <Button size="sm" onClick={() => setShowEmbedded(true)}>
                <ExternalLink className="size-4" /> Continue onboarding inline
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      {data.balance.error ? (
        <Alert>
          <AlertTitle>Live balance temporarily unavailable</AlertTitle>
          <AlertDescription>{data.balance.error}</AlertDescription>
        </Alert>
      ) : null}

      <ConnectBalanceCard available={data.balance.available} pending={data.balance.pending} />

      <ConnectPayoutsCard rows={data.payouts} />

      <RecentConnectPaymentsCard rows={data.recentPayments} />

      <RefundsCard rows={data.refunds} />

      <StripeDeepDive
        showEmbedded={showEmbedded}
        onToggleEmbedded={() => setShowEmbedded((v) => !v)}
        chargesEnabled={data.chargesEnabled}
      />
    </Page>
  )
}

function ConnectBalanceCard({
  available,
  pending,
}: {
  available: Array<{ amountCents: number; currency: string }>
  pending: Array<{ amountCents: number; currency: string }>
}) {
  if (available.length === 0 && pending.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <EmptyState
            icon={Wallet}
            title="No balance yet"
            description="Once a customer pays an invoice, your Stripe balance will appear here."
          />
        </CardContent>
      </Card>
    )
  }
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <h3 className="text-base font-semibold">Balance by currency</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-md border p-3">
            <div className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Available</div>
            {available.length === 0 ? <span className="text-sm text-muted-foreground">-</span> : (
              <ul className="space-y-1 font-mono text-sm">
                {available.map((b) => (
                  <li key={b.currency}>
                    <Badge variant="default" className="mr-2">{b.currency}</Badge>
                    {formatStripeBalance(b.amountCents, b.currency)}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-md border p-3">
            <div className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Pending settlement</div>
            {pending.length === 0 ? <span className="text-sm text-muted-foreground">-</span> : (
              <ul className="space-y-1 font-mono text-sm">
                {pending.map((b) => (
                  <li key={b.currency}>
                    <Badge variant="secondary" className="mr-2">{b.currency}</Badge>
                    {formatStripeBalance(b.amountCents, b.currency)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function ConnectPayoutsCard({ rows }: { rows: ConnectPayoutRow[] }) {
  if (rows.length === 0) {
    return null
  }
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <h3 className="text-base font-semibold">Recent payouts to your bank</h3>
        <DataTable
          rows={rows}
          columns={[
            {
              header: 'Created',
              accessorKey: 'created',
              cell: ({ row }) => <span className="text-xs">{row.original.created ? formatDate(row.original.created) : '-'}</span>,
            },
            {
              header: 'Arrival',
              accessorKey: 'arrivalDate',
              cell: ({ row }) => <span className="text-xs">{row.original.arrivalDate ? formatDate(row.original.arrivalDate) : '-'}</span>,
            },
            {
              header: () => <span className="block text-right">Amount</span>,
              accessorKey: 'amountCents',
              cell: ({ row }) => (
                <span className="block text-right font-mono text-xs">
                  {formatStripeBalance(row.original.amountCents, row.original.currency)}
                </span>
              ),
            },
            {
              header: 'Method',
              accessorKey: 'method',
              cell: ({ row }) => row.original.method
                ? <Badge variant="outline">{row.original.method}</Badge> : '-',
            },
            {
              header: 'Status',
              accessorKey: 'status',
              cell: ({ row }) => {
                const v = row.original.status === 'paid' ? 'success'
                  : row.original.status === 'failed' || row.original.status === 'canceled' ? 'danger'
                  : 'secondary'
                return <Badge variant={v}>{row.original.status}</Badge>
              },
            },
          ]}
        />
      </CardContent>
    </Card>
  )
}

function RecentConnectPaymentsCard({ rows }: { rows: ConnectPaymentRow[] }) {
  if (rows.length === 0) {
    return null
  }
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <h3 className="text-base font-semibold">Recent invoice payments</h3>
        <DataTable
          rows={rows}
          columns={[
            {
              header: 'Date',
              accessorKey: 'receivedAt',
              cell: ({ row }) => <span className="text-xs">{formatDate(row.original.receivedAt)}</span>,
            },
            {
              header: 'Invoice',
              accessorKey: 'invoiceNumber',
              cell: ({ row }) => row.original.invoiceNumber ? (
                <Link
                  to={`/invoicing/invoices/${row.original.invoiceId}`}
                  className="text-primary hover:underline"
                >
                  {row.original.invoiceNumber}
                </Link>
              ) : '-',
            },
            { header: 'Customer', accessorKey: 'customerName',
              cell: ({ row }) => row.original.customerName ?? '-' },
            {
              header: () => <span className="block text-right">Gross</span>,
              accessorKey: 'grossCents',
              cell: ({ row }) => (
                <span className="block text-right font-mono text-xs">
                  {formatMoneyCents(row.original.grossCents, row.original.currency)}
                </span>
              ),
            },
            {
              header: () => <span className="block text-right">Stripe fee</span>,
              accessorKey: 'stripeFeeCents',
              cell: ({ row }) => (
                <span className="block text-right font-mono text-xs text-muted-foreground">
                  {row.original.stripeFeeCents
                    ? `−${formatMoneyCents(row.original.stripeFeeCents, row.original.currency)}`
                    : <em className="not-italic text-muted-foreground/60">-</em>}
                </span>
              ),
            },
            {
              header: () => <span className="block text-right">Refunded</span>,
              accessorKey: 'refundedCents',
              cell: ({ row }) => (
                <span className="block text-right font-mono text-xs text-destructive">
                  {row.original.refundedCents > 0
                    ? `−${formatMoneyCents(row.original.refundedCents, row.original.currency)}`
                    : <span className="text-muted-foreground/60">-</span>}
                </span>
              ),
            },
            {
              header: () => <span className="block text-right">Net</span>,
              accessorKey: 'netCents',
              cell: ({ row }) => (
                <span className="block text-right font-mono text-xs font-semibold">
                  {formatMoneyCents(row.original.netCents, row.original.currency)}
                </span>
              ),
            },
            {
              header: 'Status',
              accessorKey: 'method',
              cell: ({ row }) => (
                <span className="flex flex-wrap items-center gap-1">
                  <Badge variant="outline">{row.original.method}</Badge>
                  <RefundStatusBadge
                    refundedCents={row.original.refundedCents}
                    grossCents={row.original.grossCents}
                    currency={row.original.currency}
                  />
                </span>
              ),
            },
            {
              header: '',
              id: 'actions',
              cell: ({ row }) => (
                <RefundActionButton
                  payment={{
                    id: row.original.id,
                    invoiceNumber: row.original.invoiceNumber,
                    customerName: row.original.customerName,
                    grossCents: row.original.grossCents,
                    refundedCents: row.original.refundedCents,
                    currency: row.original.currency,
                    method: row.original.method,
                  }}
                />
              ),
            },
          ]}
        />
      </CardContent>
    </Card>
  )
}

// ── Stripe deep-dive card (Connect mode) ─────────────────────────────
//
// Three actions side-by-side:
//   1. Open Stripe Dashboard (single-use login link to the Express
//      dashboard on dashboard.stripe.com - for managing bank account,
//      tax forms, etc. outside of Bokapsys)
//   2. Show / hide embedded Stripe panels (lazy-loaded iframe widgets)
//   3. Backfill historical fees - runs once after deploy to stamp the
//      Stripe fee on payments that landed before fee-tagging shipped

function StripeDeepDive({
  showEmbedded,
  onToggleEmbedded,
  chargesEnabled,
}: {
  showEmbedded: boolean
  onToggleEmbedded: () => void
  chargesEnabled: boolean
}) {
  const { toast } = useToast()
  const openDashboard = useOpenStripeDashboard()
  const tenantId = useTenant()
  const qc = useQueryClient()
  const [backfilling, setBackfilling] = useState(false)

  const onOpenDashboard = async () => {
    try {
      const r = await openDashboard.mutateAsync()
      window.open(r.url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not open Stripe dashboard',
        description: err instanceof Error ? err.message : 'unknown',
      })
    }
  }

  const onBackfill = async () => {
    setBackfilling(true)
    try {
      const r = await paymentsApi.backfillFees()
      toast({
        title: 'Fee backfill complete',
        description: `Scanned ${r.scanned} payment${r.scanned === 1 ? '' : 's'}, stamped ${r.tagged}, skipped ${r.skipped}.`,
      })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'payments') })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Backfill failed',
        description: err instanceof Error ? err.message : 'unknown',
      })
    } finally {
      setBackfilling(false)
    }
  }

  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold">Stripe deep dive</h3>
            <p className="text-sm text-muted-foreground">
              Open the full Stripe Express dashboard, mount Stripe panels inline, or
              backfill missing fees on historical payments.
            </p>
          </div>
          <Toolbar align="end">
            <Button
              variant="secondary"
              size="sm"
              onClick={onOpenDashboard}
              disabled={openDashboard.isPending}
            >
              <ExternalLink className="size-4" /> Open Stripe Dashboard
            </Button>
            <Button variant="secondary" size="sm" onClick={onToggleEmbedded}>
              {showEmbedded ? 'Hide Stripe panels' : 'Show Stripe panels'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onBackfill}
              disabled={backfilling}
            >
              {backfilling ? 'Backfilling…' : 'Backfill fees'}
            </Button>
          </Toolbar>
        </div>
        {showEmbedded ? (
          <Suspense fallback={<LoadingSpinner />}>
            <StripeConnectEmbeddedPanel
              panels={
                chargesEnabled
                  ? ['balances', 'payouts', 'payments', 'account_management']
                  : ['onboarding', 'account_management']
              }
            />
          </Suspense>
        ) : null}
      </CardContent>
    </Card>
  )
}

// ── Refunds history card (shared by both modes) ──────────────────────

function RefundsCard({ rows }: { rows: RefundRow[] }) {
  if (rows.length === 0) return null
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <h3 className="text-base font-semibold">Refunds</h3>
        <DataTable
          rows={rows}
          columns={[
            {
              header: 'Issued',
              accessorKey: 'issuedAt',
              cell: ({ row }) => <span className="text-xs">{formatDate(row.original.issuedAt)}</span>,
            },
            {
              header: 'Invoice',
              accessorKey: 'invoiceNumber',
              cell: ({ row }) => row.original.invoiceNumber ? (
                <Link
                  to={`/invoicing/invoices/${row.original.invoiceId}`}
                  className="text-primary hover:underline"
                >
                  {row.original.invoiceNumber}
                </Link>
              ) : '-',
            },
            { header: 'Customer', accessorKey: 'customerName',
              cell: ({ row }) => row.original.customerName ?? '-' },
            {
              header: () => <span className="block text-right">Amount</span>,
              accessorKey: 'amount',
              cell: ({ row }) => (
                <span className="block text-right font-mono text-xs text-destructive">
                  −{formatMoneyCents(row.original.grossCents, row.original.currency)}
                </span>
              ),
            },
            {
              header: 'Reason',
              accessorKey: 'reasonCode',
              cell: ({ row }) => {
                const label = row.original.reasonCode
                  ? row.original.reasonCode.replace(/_/g, ' ')
                  : '-'
                return (
                  <span className="text-xs">
                    <span className="capitalize">{label}</span>
                    {row.original.reason ? (
                      <span className="ml-1 text-muted-foreground">· {row.original.reason}</span>
                    ) : null}
                  </span>
                )
              },
            },
            {
              header: 'Status',
              accessorKey: 'status',
              cell: ({ row }) => {
                const v = row.original.status === 'succeeded' ? 'success'
                  : row.original.status === 'failed' || row.original.status === 'canceled' ? 'danger'
                  : row.original.status === 'pending' ? 'default'
                  : 'secondary'
                return (
                  <span className="flex flex-col gap-0.5">
                    <Badge variant={v}>{row.original.status}</Badge>
                    {row.original.failureReason ? (
                      <span className="text-[11px] text-destructive">{row.original.failureReason}</span>
                    ) : null}
                  </span>
                )
              },
            },
          ]}
        />
      </CardContent>
    </Card>
  )
}

// Default export so React Router lazy imports work cleanly.
export default PaymentsDashboardPage
